/**
 * Context stores (RFC §5) — per-bounded-context client caches: entities by
 * URN + named list projections + declared event appliers + optimistic
 * commands. NO global state object; a store instance is created per bounded
 * context and provided app-side via IoC tokens. The aggregate remains the
 * durable source of truth — stores are caches; `PushChannel.resume()`
 * re-hydrates.
 *
 * ── apply(): THE convergence point (both planes) ──
 * SDK reads hydrate entities, `PushChannel` events fold incrementally — both
 * as versioned envelopes through the same `apply()`.
 *
 * IDEMPOTENCY LAW (NORMATIVE, RFC §6b): the store keeps
 * `lastApplied[streamId]`; `apply()` DROPS any envelope with
 * `version <= lastApplied[streamId]`, and advances `lastApplied` on every
 * accepted envelope — INCLUDING envelopes with an unknown event type or no
 * entity mapping (the envelope was consumed; only its entity effect is nil).
 * `head(streamId)` exposes the cursor for the poll backstop.
 *
 * ── appliers + urnOf ──
 * `spec.appliers[type]: (current, envelope) => E | undefined` — a returned
 * `undefined` REMOVES the entity. Appliers are pure folds and must return NEW
 * objects on change (projection equality relies on element identity). The
 * store locates `current` via `spec.urnOf(envelope)` — web-kit does not bake
 * in any streamId grammar; the app declares the mapping. `urnOf` returning
 * undefined = no entity effect (version still advances).
 *
 * ── projections ──
 * Pure functions over the entity map, recomputed per store change; a
 * projection signal notifies ONLY when its array changes content-wise
 * (same length AND same element identity at every index = no notification).
 *
 * ── optimistic commands (declared, reversible — snapshot-based) ──
 * `spec.optimistic[cmd.type]` RETURNS a declared patch
 * (`[{urn, next}]`); the store snapshots the prior entity of each touched URN,
 * applies the patch immediately (visible before the server responds), and on
 * failure restores the snapshots. Snapshot-revert was chosen over inverse
 * folds: simpler, and exact by construction.
 *
 * PINNED interleaving semantics (the RFC is silent; conservative choice —
 * AUTHORITATIVE WINS): every authoritative `apply()` bumps a per-URN
 * generation counter. Rollback restores a touched URN ONLY IF its generation
 * is unchanged since the optimistic patch; if a push/reconcile event touched
 * that URN mid-flight, the authoritative state stands and the snapshot for
 * that URN is discarded. Optimistic writes never bump the generation.
 *
 * `command()` never rejects for transport failures — it resolves
 * `{ok: false, error}` (and has rolled back). It THROWS only for programming
 * errors (no transport wired). Success envelopes reconcile through the same
 * `apply()` and respect the idempotency law (if SSE already delivered them,
 * they drop silently). In-flight commands are observable via the `pending`
 * count signal.
 */

import { signal, batch, type ReadonlySignal, type Signal } from "../core/signal";
import type { Urn } from "../core/element";
import type { DomainEventEnvelope } from "../push/envelope";

/** A command sent over the call plane. */
export interface Command {
  type: string;
  payload?: unknown;
}

/** The server's authoritative answer to a command. */
export type CommandResult =
  | { ok: true; envelopes: readonly DomainEventEnvelope[] }
  | { ok: false; error: string };

/** The call-plane seam: apps wire the typed SDK here; tests wire fakes. */
export type CommandTransport = (cmd: Command) => Promise<CommandResult>;

/** One declared optimistic entity write. `next: undefined` removes the entity. */
export interface OptimisticPatchEntry<E> {
  urn: Urn;
  next: E | undefined;
}

/** Declared optimistic fold: pure, returns the patch to apply (and revert). */
export type OptimisticFold<E> = (
  entities: ReadonlyMap<Urn, E>,
  cmd: Command
) => ReadonlyArray<OptimisticPatchEntry<E>>;

/** Event applier: pure fold from current entity + envelope. `undefined` removes. */
export type EntityApplier<E> = (
  current: E | undefined,
  envelope: DomainEventEnvelope
) => E | undefined;

/** Named projection: pure function over the entity map. */
export type Projection<E> = (entities: ReadonlyMap<Urn, E>) => readonly E[];

export interface StoreSpec<E extends { urn: Urn }> {
  /** Bounded-context name, used in error messages. */
  name: string;
  /** Map an envelope to the entity URN it affects (undefined = no entity effect). */
  urnOf: (envelope: DomainEventEnvelope) => Urn | undefined;
  /** Appliers per event `type`. Unknown types advance the version, touch nothing. */
  appliers: Readonly<Record<string, EntityApplier<E>>>;
  /** Named list projections (e.g. "matching", "aussortiert"). */
  projections?: Readonly<Record<string, Projection<E>>>;
  /** Declared optimistic folds per command `type` (optional per command). */
  optimistic?: Readonly<Record<string, OptimisticFold<E>>>;
  /** The call-plane transport. Required before `command()` is used. */
  transport?: CommandTransport;
}

export interface ContextStore<E extends { urn: Urn }> {
  /** Per-URN entity cache. Lazily created, cached — same signal per URN forever. */
  entity(urn: Urn): ReadonlySignal<E | undefined>;
  /** Named projection signal. Throws for undeclared names. */
  list(projection: string): ReadonlySignal<readonly E[]>;
  /** Fold a push-plane event or call-plane read result into the cache. */
  apply(envelope: DomainEventEnvelope): void;
  /** Issue a command: optimistic fold now, reconcile on response, roll back on error. */
  command<C extends Command>(cmd: C): Promise<CommandResult>;
  /** The idempotency cursor for a stream (the poll backstop reads this). */
  head(streamId: string): number | undefined;
  /** Count of in-flight commands. */
  readonly pending: ReadonlySignal<number>;
}

/** Notify only when length or any element identity changed. */
const shallowArrayEquals = <E>(a: readonly E[], b: readonly E[]): boolean =>
  a === b || (a.length === b.length && a.every((x, i) => x === b[i]));

export function defineStore<E extends { urn: Urn }>(
  spec: StoreSpec<E>
): ContextStore<E> {
  const entities = new Map<Urn, E>();
  const entitySignals = new Map<Urn, Signal<E | undefined>>();
  const lastApplied = new Map<string, number>();
  /** Per-URN authoritative generation (bumped by apply(), never by optimism). */
  const generation = new Map<Urn, number>();
  /** Bumped on every store change; projections derive from it. */
  const revision = signal(0);
  const pending = signal(0);
  const projectionSignals = new Map<string, ReadonlySignal<readonly E[]>>();

  const entitySignal = (urn: Urn): Signal<E | undefined> => {
    let s = entitySignals.get(urn);
    if (!s) {
      s = signal<E | undefined>(entities.get(urn));
      entitySignals.set(urn, s);
    }
    return s;
  };

  /** Write an entity + its signal. Callers wrap in batch() + bump revision. */
  const setEntity = (urn: Urn, next: E | undefined): void => {
    if (next === undefined) entities.delete(urn);
    else entities.set(urn, next);
    entitySignal(urn).set(next);
  };

  const apply = (envelope: DomainEventEnvelope): void => {
    const last = lastApplied.get(envelope.streamId) ?? 0;
    if (envelope.version <= last) return; // IDEMPOTENCY LAW: drop
    lastApplied.set(envelope.streamId, envelope.version);

    const applier = spec.appliers[envelope.type];
    if (!applier) return; // unknown type: consumed, no entity effect
    const urn = spec.urnOf(envelope);
    if (!urn) return; // no entity mapping: consumed, no entity effect

    batch(() => {
      setEntity(urn, applier(entities.get(urn), envelope));
      generation.set(urn, (generation.get(urn) ?? 0) + 1);
      revision.update((r) => r + 1);
    });
  };

  interface Snapshot {
    urn: Urn;
    prev: E | undefined; // undefined = did not exist (restore = delete)
    gen: number;
  }

  const rollback = (snapshots: readonly Snapshot[]): void => {
    batch(() => {
      for (const { urn, prev, gen } of snapshots) {
        // AUTHORITATIVE WINS: skip URNs an apply() touched mid-flight.
        if ((generation.get(urn) ?? 0) !== gen) continue;
        setEntity(urn, prev);
      }
      revision.update((r) => r + 1);
    });
  };

  const command = async <C extends Command>(cmd: C): Promise<CommandResult> => {
    const { transport } = spec;
    if (!transport) {
      throw new Error(
        `[web-kit] store "${spec.name}": command("${cmd.type}") called but the ` +
          `spec has no transport. Wire the call-plane CommandTransport in the spec.`
      );
    }

    let snapshots: readonly Snapshot[] = [];
    const fold = spec.optimistic?.[cmd.type];
    if (fold) {
      const patch = fold(entities, cmd);
      snapshots = patch.map(({ urn }) => ({
        urn,
        prev: entities.get(urn),
        gen: generation.get(urn) ?? 0,
      }));
      batch(() => {
        for (const { urn, next } of patch) setEntity(urn, next); // no generation bump
        revision.update((r) => r + 1);
      });
    }

    pending.update((n) => n + 1);
    try {
      const result = await transport(cmd);
      if (result.ok) {
        for (const envelope of result.envelopes) apply(envelope); // reconcile; law dedupes
      } else {
        rollback(snapshots);
      }
      return result;
    } catch (e) {
      rollback(snapshots);
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    } finally {
      pending.update((n) => n - 1);
    }
  };

  return {
    entity: (urn) => entitySignal(urn),

    list(name: string): ReadonlySignal<readonly E[]> {
      let s = projectionSignals.get(name);
      if (!s) {
        const project = spec.projections?.[name];
        if (!project) {
          throw new Error(
            `[web-kit] store "${spec.name}": unknown projection "${name}". ` +
              `Declared: ${Object.keys(spec.projections ?? {}).join(", ") || "(none)"}.`
          );
        }
        s = (() => {
          const derived = signal<readonly E[]>(project(entities), {
            equals: shallowArrayEquals,
          });
          revision.subscribe(() => derived.set(project(entities)));
          return derived;
        })();
        projectionSignals.set(name, s);
      }
      return s;
    },

    apply,
    command,
    head: (streamId) => lastApplied.get(streamId),
    pending,
  };
}
